// Whether the panel's offer list should follow the task's saved slots, be
// cleared, or be left alone.
//
// The list is seeded from aiScheduling.availableSlots so that reopening a task
// shows the times that were sent. That effect used to re-run whenever the saved
// array changed identity — which, once the task began polling, was every 15
// seconds — and each run threw away anything added by hand. Found on a phone:
// add a time, reach for Book, and it was gone.
//
// Three outcomes, in priority order. Booking ends the offer regardless. A list
// somebody has touched is theirs. Otherwise it mirrors the task.

export type OfferSeedAction = 'clear' | 'keep' | 'seed';

export function offerSeedAction({
  status,
  booked,
  touched,
  rescheduling = false,
  stepsLeft = false,
}: {
  status?: string | null;
  booked: boolean;
  touched: boolean;
  /** The office pressed "Offer different times" on a booked task. */
  rescheduling?: boolean;
  /** One step of a set was just booked from the panel and the others are still listed. */
  stepsLeft?: boolean;
}): OfferSeedAction {
  // The lab draw is booked and the doctor's visit is still to book: the
  // office books them one after the other, so the list stays.
  if (stepsLeft) return 'keep';
  if (booked) return 'clear';
  // A confirmed appointment ends the offer that led to it — but not the new
  // one the office is building to move it. Without this exception every time
  // picked while rescheduling was wiped on the next poll, so a booked patient
  // could not be offered different times from the panel at all.
  if (status === 'appointment_confirmed') return rescheduling && touched ? 'keep' : 'clear';
  if (touched) return 'keep';
  // A cancellation ends the old offer too: what is saved on the task is the
  // list the patient was sent before they booked, and re-seeding it put five
  // stale times - and a hold warning about them - in front of the office.
  // Below `touched` on purpose: once the office starts a new list after the
  // cancellation, the poll must not clear it every fifteen seconds.
  if (status === 'appointment_cancelled') return 'clear';
  return 'seed';
}
